import React from "react";
//#region types
import { DialogContentHandles, DialogLayoutProps } from "models/propTypes/dialogPropTypes";
import { CommonEnums, StyleEnums } from "models/enums";
import { FormHandles } from "models/propTypes/genericFormProps";
import { FileConstants, HTMLConstants } from "utils/constants";
//#endregion
//#region components
import { Dialog, DialogTitle, Grid, DialogContent, Divider, IconButton, Portal } from "@mui/material";
import useCloseConfirmation from "./useCloseConfirmation";
import useFilePreviewOnDialog from "../fileViewer/usePreviewOnDialog";
import useDialogOnEnter from "./useOnEnter";
import FormValidity from "../forms/formValidity";
import * as Transitions from "../transitions";
import Icon from "../icon";
import CloseIcon from "@mui/icons-material/Close";
import { ErrorBoundary } from "components/error";
import DialogFooter from "./dialogFooter";
import DialogPreviewPortal from "./dialogPreviewPortal";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const DialogLayout: React.FC<DialogLayoutProps> = (props) => {
  const innerRef = React.useRef<FormHandles & DialogContentHandles>(null);

  const mount = document.getElementById(HTMLConstants.LayoutPortalId);

  const onClose = async () => {
    innerRef.current?.onClose && innerRef.current.onClose();
    props.onClose && props.onClose();
    props.closeDialog && props.closeDialog();
  };

  const { showConfirmation, showCustomConfirmation } = useCloseConfirmation();

  const isFormDirty = () => {
    if (innerRef.current?.isDirty) {
      return innerRef.current.isDirty();
    }
    if (innerRef.current?.getTouched) {
      return Object.entries(innerRef.current.getTouched()).length > 0;
    }
    return false;
  };

  const confirmThenClose = () => {
    if (isFormDirty()) {
      if (props.customCloseConfirmation) {
        showCustomConfirmation(props.customCloseConfirmation.title, props.customCloseConfirmation.content, onClose);
      } else {
        showConfirmation(onClose);
      }
    } else {
      onClose();
    }
  };

  const onClickOutSide = () => {
    if (props.preventClickOutsideClose) return;
    confirmThenClose();
  };

  // Explicit close intent via the X. Always proceeds (does not respect preventClickOutsideClose),
  // but still prompts confirmation when the form has touched fields and the dialog opted in.
  const onXClose = props.confirmCloseOnX ? confirmThenClose : onClose;

  // MUI only sees Esc while focus is inside the dialog; a handled Esc (e.g. an open date picker) is left alone.
  React.useEffect(() => {
    if (!props.isOpen || !props.closeOnEscapeAnywhere) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) onClickOutSide();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [props.isOpen, props.closeOnEscapeAnywhere, props.preventClickOutsideClose]);

  const { assignPreviewRef, assignRef, containerDimensions } = useFilePreviewOnDialog();

  const childElement = React.Children.only(props.children);

  useDialogOnEnter(props.isOpen, props.onEnterHandler);

  const submitFormContent = () => {
    return innerRef.current?.submit ? innerRef.current.submit() : Promise.resolve();
  };

  return (
    <>
      <Portal container={mount}>{props.overlayAnimationComponent}</Portal>
      {props.previewElement && <DialogPreviewPortal previewElement={props.previewElement} />}
      <FormValidity
        render={(isValid, onValidityChange) => (
          <Dialog
            id="dialogRoot"
            ref={assignRef}
            aria-labelledby="dialog-title"
            open={props.isOpen}
            maxWidth={props.maxWidth ?? "sm"}
            fullWidth={props.fullWidth}
            className={clsx(
              props.class,
              props.type ? (props.type === StyleEnums.DisplayType.Danger ? cssExports.danger : cssExports.warning) : ""
            )}
            classes={{
              root: clsx(cssExports.dialogRoot, props.previewElement && cssExports.dialogWithPreview),
              paperScrollPaper: clsx(
                cssExports.dialogScrollPaper,
                (props.minWidth && cssExports[props.minWidth]) || cssExports.sm
              ),
              paperWidthMd: cssExports.dialogMaxMd,
              paperWidthSm: cssExports.dialogMaxSm,
            }}
            TransitionComponent={Transitions.DialogTransition}
            onClose={onClickOutSide}
            closeAfterTransition
          >
            <ErrorBoundary subComponentType={CommonEnums.SubComponentType.Dialog}>
              <Grid
                ref={assignPreviewRef}
                id={
                  props.previewElement ? FileConstants.DialogPreviewElementId : FileConstants.DialogFilePreviewPortalId
                }
                className={cssExports.filePreviewOverlay}
                style={
                  !props.previewElement
                    ? {
                        width: containerDimensions ? `${containerDimensions}px` : undefined,
                      }
                    : undefined
                }
              ></Grid>
              {!props.hideCloseButton ? (
                <IconButton aria-label="close" className={cssExports.closeButton} onClick={onXClose}>
                  <CloseIcon />
                </IconButton>
              ) : (
                <></>
              )}
              <DialogTitle id="dialog-title" className={cssExports.dialogHeader}>
                <Grid container className={cssExports.dialogTitleOuter} alignItems="center">
                  <Grid item>
                    {props.titleIcon ? (
                      <span className={cssExports.icon}>
                        <Icon icon={props.titleIcon} />
                      </span>
                    ) : (
                      ""
                    )}
                  </Grid>
                  <Grid item xs>
                    <Grid item className={props.subTitle ? cssExports.dialogTitleWithSubTitle : cssExports.dialogTitle}>
                      {props.title}
                    </Grid>
                    <Grid item className={cssExports.dialogSubTitle}>
                      {props.subTitle}
                    </Grid>
                  </Grid>
                </Grid>
                {props.header}
              </DialogTitle>
              {props.hideDivider ? <></> : <Divider className={cssExports.dialogDivider} />}
              <ErrorBoundary subComponentType={CommonEnums.SubComponentType.Dialog}>
                <DialogContent className={cssExports.dialogContent}>
                  {React.cloneElement(childElement, {
                    ref: innerRef,
                    onFormValidationChange: onValidityChange,
                  })}
                </DialogContent>
                {!props.hideFooter && (
                  <DialogFooter
                    {...props}
                    isValid={isValid}
                    submitFormContent={submitFormContent}
                    closeView={props.closeDialog}
                    onClose={onClose}
                  />
                )}
              </ErrorBoundary>
            </ErrorBoundary>
          </Dialog>
        )}
      />
    </>
  );
};

export default DialogLayout;
